/* ==============================================================================
   Raiz do cliente
   ==============================================================================
   Decide entre três estados: carregando, autenticado, não autenticado.

   O que estava quebrado antes: depois de um login bem-sucedido o código chamava
   `window.location.assign('/')`, a página recarregava e voltava para o
   formulário — porque não havia restauração de sessão NEM tela autenticada. O
   login funcionava no servidor e não levava a lugar nenhum no cliente.

   Agora o fluxo é: no boot tenta restaurar a sessão pelo cookie de refresh; se
   houver, renderiza o app; se não, o login. E o login troca o estado em memória
   em vez de recarregar a página — recarregar jogaria fora o access token que
   acabou de ser recebido.
   ============================================================================== */

import { useCallback, useEffect, useState } from 'react';
import {
  DialogProvider,
  LoginScreen,
  RegisterScreen,
  type LoginStep,
} from '@concord/ui';

import { AppShell } from './screens/AppShell.tsx';
import {
  adoptSession,
  logout as endSession,
  restoreSession,
  startTokenRenewal,
  type CurrentUser,
} from './session.ts';

/**
 * Estado do back-end.
 *
 * `registrationOpen` vem do próprio `/health`: a interface não deve oferecer
 * "criar conta" se o servidor tem ALLOW_OPEN_REGISTRATION=false — mostrar o
 * botão e receber 403 é pior que não mostrar.
 */
type BackendState =
  | { status: 'checking' }
  | { status: 'online'; version: string; registrationOpen: boolean }
  | { status: 'offline'; reason: string };

type Screen = 'login' | 'register';

const HEALTH_TIMEOUT_MS = 4_000;

async function checkBackend(): Promise<BackendState> {
  // AbortController em vez de esperar o timeout do navegador: sem isto a tela
  // ficaria "verificando" por 30s ou mais quando não há nada escutando.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);

  try {
    const response = await fetch('/api/v1/health', {
      signal: controller.signal,
      headers: { accept: 'application/json' },
    });

    if (!response.ok) {
      return {
        status: 'offline',
        reason: `A API respondeu ${response.status}.`,
      };
    }

    const body: unknown = await response.json().catch(() => null);
    const read = (key: string): unknown =>
      body && typeof body === 'object' ? (body as Record<string, unknown>)[key] : undefined;

    return {
      status: 'online',
      version: String(read('version') ?? 'desconhecida'),
      // Default `true`: se o campo não existir (servidor mais antigo), é melhor
      // oferecer o cadastro e receber um 403 explicativo do que esconder a
      // única forma de entrar.
      registrationOpen: read('registration_open') !== false,
    };
  } catch (error) {
    const aborted = error instanceof DOMException && error.name === 'AbortError';
    return {
      status: 'offline',
      reason: aborted
        ? `A API não respondeu em ${HEALTH_TIMEOUT_MS / 1000}s.`
        : 'Nenhuma resposta da API.',
    };
  } finally {
    clearTimeout(timer);
  }
}

/** Lê `{error, message}` da resposta, com fallback por status. */
async function readError(response: Response, fallback: string): Promise<string> {
  const body: unknown = await response.json().catch(() => null);
  if (
    body &&
    typeof body === 'object' &&
    typeof (body as { message?: unknown }).message === 'string'
  ) {
    return (body as { message: string }).message;
  }
  return fallback;
}

export function App() {
  const [booting, setBooting] = useState(true);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [backend, setBackend] = useState<BackendState>({ status: 'checking' });
  const [screen, setScreen] = useState<Screen>('login');
  const [step, setStep] = useState<LoginStep>('credentials');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  /* --------------------------------------------------------------------------
     Boot: restaura a sessão antes de decidir o que renderizar.
     -------------------------------------------------------------------------- */
  useEffect(() => {
    let active = true;
    void (async () => {
      // Em paralelo: saber se a API está viva e tentar restaurar a sessão. Em
      // série custaria duas viagens sequenciais na abertura do app.
      const [state, restored] = await Promise.all([checkBackend(), restoreSession()]);
      if (!active) return;
      setBackend(state);
      setUser(restored);
      setBooting(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  /* --------------------------------------------------------------------------
     Sonda periódica da API, só enquanto NÃO autenticado.
     Autenticado, o próprio uso revela a queda; sondar em laço seria ruído.
     -------------------------------------------------------------------------- */
  useEffect(() => {
    if (user) return;
    let active = true;
    const interval = setInterval(() => {
      void checkBackend().then((state) => {
        if (active) setBackend(state);
      });
    }, 15_000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [user]);

  /* --------------------------------------------------------------------------
     Renovação de fundo do access token enquanto a sessão estiver ativa.
     Sem isto, uma aba aberta por mais de 10 min só descobriria o token expirado
     no próximo clique — que então travaria por uma renovação síncrona.
     -------------------------------------------------------------------------- */
  useEffect(() => {
    if (!user) return;
    return startTokenRenewal((reason) => {
      /*
       * "Expirou" e "não deu para renovar" são coisas DIFERENTES.
       *
       * A versão anterior tratava as duas como expiração e mandava a pessoa
       * entrar de novo. Um 429 no `/auth/refresh` — que é limitado por IP,
       * porque a rota não envia `Authorization` — bastava para deslogar alguém
       * com sessão perfeitamente válida. Aconteceu em produção: uma bateria de
       * testes saindo pelo mesmo IP público drenou o balde de limite, e o
       * navegador foi expulso.
       *
       * `unavailable` mantém a pessoa DENTRO do app. A renovação se reagenda
       * sozinha em 15 s, e o access token em memória continua servindo enquanto
       * isso — a renovação dispara 30 s antes de ele expirar, então há folga.
       */
      if (reason === 'expired') {
        setUser(null);
        setError('Sua sessão expirou. Entre novamente.');
        return;
      }

      setError(
        'Não foi possível renovar a sessão agora. Continuando com a sessão ' +
          'atual e tentando de novo em alguns segundos.',
      );
    });
  }, [user]);

  const online = backend.status === 'online';

  /** Troca de tela limpa o erro: mensagem de login não pertence ao cadastro. */
  const goTo = useCallback((next: Screen) => {
    setScreen(next);
    setError(null);
    setStep('credentials');
  }, []);

  /**
   * Consome a resposta de login/cadastro.
   *
   * Guarda o token em MEMÓRIA e troca o estado do React — sem
   * `window.location.assign`. Recarregar jogaria fora o access token recém
   * recebido e forçaria um refresh desnecessário; era também a razão pela qual
   * o login "não levava a lugar nenhum".
   */
  const consumeAuth = useCallback(async (response: Response): Promise<boolean> => {
    const body = (await response.json()) as {
      accessToken?: string;
      expiresIn?: number;
      user?: unknown;
    };
    if (!body.accessToken || typeof body.expiresIn !== 'number') return false;

    adoptSession(body.accessToken, body.expiresIn);

    // Busca o perfil completo em vez de usar o resumo da resposta de auth: é a
    // MESMA fonte que o boot usa, então a tela do app recebe sempre o mesmo
    // formato, sem um caminho paralelo para manter em sincronia.
    const restored = await restoreSession();
    if (!restored) return false;
    setUser(restored);
    return true;
  }, []);

  const submitLogin = useCallback(
    async (email: string, password: string) => {
      if (!online) {
        setError('O serviço de autenticação ainda não está disponível.');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/v1/auth/login', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ email, password }),
        });

        if (response.ok) {
          if (!(await consumeAuth(response))) {
            setError('Entrou, mas não foi possível carregar seu perfil.');
          }
          return;
        }

        const body: unknown = await response.clone().json().catch(() => null);
        if (body && typeof body === 'object' && (body as { mfa_required?: boolean }).mfa_required) {
          setStep('totp');
          return;
        }
        // A mensagem vem do servidor, que já a escreve de forma genérica para
        // não permitir enumerar contas. Não reescrever aqui.
        setError(await readError(response, 'Não foi possível entrar.'));
      } catch {
        setError('Falha de rede ao contatar o servidor.');
      } finally {
        setLoading(false);
      }
    },
    [online, consumeAuth],
  );

  const submitTotp = useCallback(
    async (code: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/v1/auth/mfa/totp', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ code }),
        });
        if (response.ok) {
          await consumeAuth(response);
          return;
        }
        setError(await readError(response, 'Código inválido ou expirado.'));
      } catch {
        setError('Falha de rede ao verificar o código.');
      } finally {
        setLoading(false);
      }
    },
    [consumeAuth],
  );

  const submitRegister = useCallback(
    async (input: {
      email: string;
      username: string;
      password: string;
      displayName?: string;
    }) => {
      if (!online) {
        setError('O serviço de autenticação ainda não está disponível.');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/v1/auth/register', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(input),
        });

        if (response.ok) {
          if (!(await consumeAuth(response))) {
            setError('Conta criada, mas não foi possível carregar seu perfil.');
          }
          return;
        }
        setError(await readError(response, 'Não foi possível criar a conta.'));
      } catch {
        setError('Falha de rede ao criar a conta.');
      } finally {
        setLoading(false);
      }
    },
    [online, consumeAuth],
  );

  /*
   * `logout` estável, e a instabilidade anterior não era cosmética.
   *
   * Antes: `onLogout={() => void handleLogout()}`. Uma arrow inline é uma
   * FUNÇÃO NOVA em cada render de App — e o AppShell tem `onLogout` nas
   * dependências do efeito que cria o WebSocket e o motor de voz. Cada render
   * derrubava a conexão e DESCARTAVA o motor, o que no meio de uma chamada
   * significa: a linha em `voice_states` fica no servidor, o cliente perde o
   * estado, e a interface mostra você no canal sem nenhum controle.
   *
   * Era um dos motivos de "estou dentro mas não aparece nada".
   */
  const handleLogoutInternal = useCallback(async () => {
    await endSession();
    setUser(null);
    setScreen('login');
    setError(null);
  }, []);

  const logout = useCallback(() => {
    void handleLogoutInternal();
  }, [handleLogoutInternal]);

  /* --------------------------------------------------------------------------
     Render
     -------------------------------------------------------------------------- */

  // Tela de boot: evita o piscar do formulário de login para quem JÁ está
  // autenticado. Sem ela, todo recarregamento mostraria o login por um instante.
  if (booting) return <BootScreen />;

  /*
   * O DialogProvider envolve SÓ a árvore autenticada.
   *
   * As telas de login não usam diálogo — e montar o provider ali adicionaria um
   * contexto que nada consome, mais um nível de árvore no caminho mais
   * sensível a tempo de carregamento do app.
   */
  if (user) {
    return (
      <DialogProvider>
        <AppShell user={user} onLogout={logout} />
      </DialogProvider>
    );
  }

  const offlineMessage =
    backend.status === 'offline'
      ? 'Indisponível: o serviço de autenticação não está no ar.'
      : null;

  return (
    <>
      {!online && <BackendPill state={backend} />}

      {screen === 'register' ? (
        <RegisterScreen
          onSubmit={submitRegister}
          onLoginClick={() => goTo('login')}
          error={error ?? offlineMessage}
          loading={loading}
          disabled={!online}
        />
      ) : (
        <LoginScreen
          step={step}
          error={error ?? offlineMessage}
          /* `loading` e `disabled` são coisas diferentes: loading mostra spinner
             ("processando"), disabled só bloqueia ("não dá para tentar agora"). */
          loading={loading}
          disabled={!online}
          allowRegistration={online && backend.registrationOpen}
          onRegisterClick={() => goTo('register')}
          onSubmitCredentials={submitLogin}
          onSubmitTotp={submitTotp}
          onOidcLogin={(provider) => {
            window.location.assign(`/api/v1/auth/oidc/${provider}/start`);
          }}
        />
      )}
    </>
  );
}

/** Fundo neutro com a marca, no tempo do boot. Sem spinner: ele chamaria
 *  atenção para uma espera que costuma durar menos que a percepção. */
function BootScreen() {
  return (
    <div
      className="grid min-h-screen w-full place-items-center"
      style={{ background: 'var(--color-surface-sunken)' }}
    >
      <span
        style={{
          fontSize: 'var(--text-sm)',
          color: 'var(--color-text-disabled)',
          letterSpacing: '0.02em',
        }}
      >
        Concord
      </span>
    </div>
  );
}

/** Pílula de status no canto. Informa sem competir com o formulário. */
function BackendPill({ state }: { state: BackendState }) {
  const checking = state.status === 'checking';
  return (
    <div
      role="status"
      aria-live="polite"
      title={state.status === 'offline' ? state.reason : undefined}
      className="fixed bottom-4 right-4 flex items-center gap-2 px-3 py-2"
      style={{
        zIndex: 'var(--z-toast)',
        borderRadius: 'var(--radius-full)',
        background: 'oklch(100% 0 0 / 0.9)',
        border: '1px solid oklch(0% 0 0 / 0.08)',
        fontSize: 'var(--text-2xs)',
        color: 'oklch(30% 0.02 265)',
        boxShadow: '0 2px 10px oklch(0% 0 0 / 0.16)',
      }}
    >
      <span
        aria-hidden="true"
        className="inline-block size-1.5 shrink-0 rounded-full"
        style={{ background: checking ? 'oklch(60% 0.01 265)' : 'oklch(70% 0.18 60)' }}
      />
      {checking ? 'Verificando o servidor…' : 'API indisponível'}
    </div>
  );
}
